import React, { useRef }              from 'react'
import { Box, Button, Typography }    from '@mui/material'
import { Download }                   from '@mui/icons-material'
import { QRCodeCanvas }               from 'qrcode.react'

/*
 * QR code for a welcome-message link, with the Birdsy chat avatar in the middle (085).
 *
 * Two canvases are rendered: one on screen, and an offscreen one at print resolution that the
 * download reads from. Scaling the small canvas up would blur it, and QR codes are usually
 * printed rather than viewed.
 *
 * Colours are fixed black on white rather than themed. A dark-mode QR would export a dark PNG,
 * and scanners expect a dark code on a light ground.
 */

// the avatar used by the chat (prepkit/chat/Message.js), so the code matches the tutor
const AVATAR_SRC = '/birdsy-avatar-black.png'

const DISPLAY_SIZE = 220
const DOWNLOAD_SIZE = 1024

/*
 * Level H tolerates ~30% of the code being unreadable. The avatar covers ~5% by area at 22% of
 * the width, so there is plenty of headroom — `excavate` clears the modules underneath rather
 * than drawing over live data.
 */
const ERROR_CORRECTION = 'H'
const AVATAR_RATIO = 0.22

const avatarSettings = (size) => ({
	src: AVATAR_SRC,
	height: Math.round(size * AVATAR_RATIO),
	width: Math.round(size * AVATAR_RATIO),
	excavate: true,
})

const WelcomeMessageQr = ({ url, token }) => {
	const downloadCanvasRef = useRef(null)

	const handleDownload = () => {
		const canvas = downloadCanvasRef.current
		if (!canvas) return

		const link = document.createElement('a')
		link.download = `birdsy-${(token || 'login').toLowerCase()}.png`
		link.href = canvas.toDataURL('image/png')
		link.click()
	}

	return (
		<Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
			<Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 2, lineHeight: 0 }}>
				<QRCodeCanvas
					value={url}
					size={DISPLAY_SIZE}
					level={ERROR_CORRECTION}
					marginSize={2}
					bgColor="#FFFFFF"
					fgColor="#000000"
					imageSettings={avatarSettings(DISPLAY_SIZE)}
					title={`QR code for ${url}`}
				/>
			</Box>

			<Button size="small" startIcon={<Download />} onClick={handleDownload}>
				Download PNG
			</Button>

			<Typography variant="caption" color="text.secondary">
				{DOWNLOAD_SIZE}&times;{DOWNLOAD_SIZE}px
			</Typography>

			{/*
			  * Offscreen, not display:none — a canvas that is never laid out can still be read,
			  * but keeping it in flow avoids relying on that.
			  */}
			<Box sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden>
				<QRCodeCanvas
					ref={downloadCanvasRef}
					value={url}
					size={DOWNLOAD_SIZE}
					level={ERROR_CORRECTION}
					marginSize={2}
					bgColor="#FFFFFF"
					fgColor="#000000"
					imageSettings={avatarSettings(DOWNLOAD_SIZE)}
				/>
			</Box>
		</Box>
	)
}

export default WelcomeMessageQr
